import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../api';
import Footer from '../components/Footer';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './GoogleConnectPage.module.css';
import Navbar, { BrandIcon } from '../components/Navbar';

// ── Inline SVG icon helper ──────────────────────────────────────────────────
const Icon = ({ d, size = 20, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    {Array.isArray(d)
      ? d.map((path, i) => <path key={i} d={path} />)
      : <path d={d} />}
  </svg>
);

// ── Google "G" logo SVG ─────────────────────────────────────────────────────
const GoogleLogo = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

// ── Spinner ─────────────────────────────────────────────────────────────────
const Spinner = ({ size = 20 }) => (
  <svg
    className={styles.spinner}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

// ── Step cards data ─────────────────────────────────────────────────────────
const STEPS = [
  {
    num: 1,
    title: 'Click "Connect Google"',
    desc: "You'll be securely redirected to Google's authorization page.",
    icon: ['M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4', 'M10 17l5-5-5-5', 'M15 12H3'],
    color: '#4285F4',
  },
  {
    num: 2,
    title: 'Grant Permissions',
    desc: 'Google will ask you to allow access to view and reply to your Business Profile reviews.',
    icon: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    color: '#34A853',
  },
  {
    num: 3,
    title: 'Google Sends a Code',
    desc: "After you approve, Google redirects back to our app with a secure authorization code.",
    icon: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
    color: '#FBBC05',
  },
  {
    num: 4,
    title: 'Tokens Saved Securely',
    desc: "We exchange the code for access tokens and store them encrypted — ready to fetch and reply to reviews.",
    icon: ['M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-1 14.93V17a1 1 0 0 1 2 0v-.07A8 8 0 0 0 19.93 11H20a1 1 0 0 1 0 2h-.07A8 8 0 0 0 13 19.93V20a1 1 0 0 1-2 0v-.07A8 8 0 0 0 4.07 13H4a1 1 0 0 1 0-2h.07A8 8 0 0 0 11 4.07V4a1 1 0 0 1 2 0v.07A8 8 0 0 0 19.93 11', 'M12 2v4M12 18v4M2 12h4M18 12h4'],
    color: '#EA4335',
  },
];

const PERMISSIONS = [
  { icon: 'M9 11l3 3L22 4', label: 'View your Google Business Profile locations' },
  { icon: 'M9 11l3 3L22 4', label: 'Read all customer reviews on your profile' },
  { icon: 'M9 11l3 3L22 4', label: 'Post replies to reviews on your behalf' },
  { icon: 'M9 11l3 3L22 4', label: 'View basic account information (name, email)' },
];

// ── Main Component ──────────────────────────────────────────────────────────
const GoogleConnectPage = () => {
  const { t } = useLanguage();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  const token = localStorage.getItem('token1');
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user1')) || null; }
    catch { return null; }
  })();

  // Check if the user already has a connected Google account
  useEffect(() => {
    const checkConnection = async () => {
      if (!token) { setLoadingStatus(false); return; }
      try {
        const res = await fetch(getApiUrl('/api/auth/google/locations'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.locations && data.locations.length > 0) {
            setConnectedAccount(data.locations[0]);
          }
        }
      } catch {
        // Silently ignore — backend may not have this route yet
      } finally {
        setLoadingStatus(false);
      }
    };
    checkConnection();
  }, [token]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl('/api/auth/google/url'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Server error (${res.status})`);
      }
      const { url } = await res.json();
      // Hard redirect to Google OAuth consent screen
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to initiate Google connection. Make sure the backend is running.');
      setConnecting(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* ── Navbar ───────────────────────────────────────────────── */}
      <Navbar commonNavigation className={styles.navbar} innerClassName={styles.navInner} brandClassName={styles.navBrand} brand={<BrandIcon size={24} style={{ marginRight: 6 }} />}>
          <div className={styles.navActions}>
            <LanguageToggle />
            {user && (
              <span className={styles.navUser}>
                <Icon d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" size={15} />
                {user.name || user.email}
              </span>
            )}
            <Link to="/dashboard" className={styles.navLink}>
              {t('dashboard', 'Dashboard')}
            </Link>
          </div>
      </Navbar>

      <main className={styles.main}>
        {/* ── Hero ─────────────────────────────────────────────── */}
        <section className={styles.hero}>
          <div className={styles.container}>
            <div className={styles.heroBadge}>
              <GoogleLogo size={16} />
              {t('googleConnectTag', 'Google Business Profile')}
            </div>
            <h1 className={styles.heroTitle}>
              {t('connectGoogleAccount', 'Connect Your Google Account')}
            </h1>
            <p className={styles.heroSub}>
              {t('googleConnectSub', 'Authorise ReplyShoot to securely fetch your reviews and post AI-generated replies — all on your behalf, 24/7.')}
            </p>

            {/* ── Connection status ──────────────────────────────── */}
            {loadingStatus ? (
              <div className={styles.statusCard}>
                <Spinner size={18} />
                <span>{t('checkingConnectionStatus', 'Checking connection status…')}</span>
              </div>
            ) : connectedAccount ? (
              <div className={`${styles.statusCard} ${styles.statusConnected}`}>
                <Icon d="M9 11l3 3L22 4" size={18} className={styles.statusIcon} />
                <div>
                  <div className={styles.statusTitle}>{t('googleAccountConnected', 'Google Account Connected')}</div>
                  <div className={styles.statusSub}>
                    {connectedAccount.name || connectedAccount.googleAccountId}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.reconnectBtn}
                  onClick={handleConnect}
                  disabled={connecting}
                >
                  {t('reconnectBtn', 'Reconnect')}
                </button>
              </div>
            ) : (
              <div className={`${styles.statusCard} ${styles.statusDisconnected}`}>
                <Icon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" size={18} className={styles.statusIcon} />
                <div>
                  <div className={styles.statusTitle}>{t('noGoogleAccountConnected', 'No Google Account Connected')}</div>
                  <div className={styles.statusSub}>{t('followStepsBelow', 'Follow the steps below to get started.')}</div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── How it works ─────────────────────────────────────── */}
        <section className={styles.section}>
          <div className={styles.container}>
            <div className={styles.sectionLabel}>How It Works</div>
            <h2 className={styles.sectionTitle}>Four simple steps to connect</h2>
            <div className={styles.stepsGrid}>
              {STEPS.map((step, idx) => (
                <div className={styles.stepCard} key={step.num} style={{ '--step-color': step.color }}>
                  <div className={styles.stepNum}>{step.num}</div>
                  <div className={styles.stepIconWrap} style={{ background: `${step.color}18`, borderColor: `${step.color}33` }}>
                    <Icon d={step.icon} size={22} style={{ color: step.color }} />
                  </div>
                  <div className={styles.stepTitle}>{step.title}</div>
                  <div className={styles.stepDesc}>{step.desc}</div>
                  {idx < STEPS.length - 1 && <div className={styles.stepConnector} />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Permissions ──────────────────────────────────────── */}
        <section className={styles.section}>
          <div className={styles.container}>
            <div className={styles.permissionsWrap}>
              <div className={styles.permissionsLeft}>
                <div className={styles.sectionLabel}>Permissions</div>
                <h2 className={styles.sectionTitle}>What we'll have access to</h2>
                <p className={styles.permissionsDesc}>
                  Google will show you exactly what our app is requesting. You can revoke
                  access at any time from your{' '}
                  <a
                    href="https://myaccount.google.com/permissions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.textLink}
                  >
                    Google Account settings
                  </a>.
                </p>
                <ul className={styles.permList}>
                  {PERMISSIONS.map((p, i) => (
                    <li key={i} className={styles.permItem}>
                      <span className={styles.permCheck}>
                        <Icon d={p.icon} size={14} />
                      </span>
                      {p.label}
                    </li>
                  ))}
                </ul>
              </div>

              <div className={styles.permissionsRight}>
                <div className={styles.mockConsentCard}>
                  <div className={styles.mockConsentHeader}>
                    <GoogleLogo size={22} />
                    <span>Google</span>
                  </div>
                  <div className={styles.mockConsentTitle}>
                    ReplyShoot wants access to your Google Account
                  </div>
                  <div className={styles.mockConsentSub}>
                    <strong>ReplyShoot</strong> will be able to:
                  </div>
                  <ul className={styles.mockConsentList}>
                    <li>View and manage your Business Profile locations</li>
                    <li>Read and respond to reviews</li>
                    <li>View your account info (name &amp; email)</li>
                  </ul>
                  <div className={styles.mockConsentFooter}>
                    <div className={styles.mockConsentEmail}>
                      {user?.email || 'you@example.com'}
                    </div>
                    <div className={styles.mockConsentBtns}>
                      <span className={styles.mockDenyBtn}>Deny</span>
                      <span className={styles.mockAllowBtn}>Allow</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────── */}
        <section className={styles.ctaSection}>
          <div className={styles.container}>
            <div className={styles.ctaCard}>
              <h2 className={styles.ctaTitle}>Ready to connect?</h2>
              <p className={styles.ctaSub}>
                You'll be taken to Google's secure authorization page. It takes under 30 seconds.
              </p>

              {error && (
                <div className={styles.errorAlert} role="alert">
                  <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" size={16} />
                  {error}
                </div>
              )}

              {!token ? (
                <div className={styles.loginPrompt}>
                  <Icon d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" size={18} />
                  <span>Please log in before connecting your Google account.</span>
                </div>
              ) : (
                <button
                  id="connect-google-btn"
                  type="button"
                  className={styles.connectBtn}
                  onClick={handleConnect}
                  disabled={connecting}
                >
                  {connecting ? (
                    <>
                      <Spinner size={20} />
                      Redirecting to Google…
                    </>
                  ) : (
                    <>
                      <GoogleLogo size={20} />
                      Connect Google Business Profile
                    </>
                  )}
                </button>
              )}

              <p className={styles.ctaFootnote}>
                <Icon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" size={13} />
                Your credentials are never stored in plain text. We only store encrypted OAuth tokens.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer>
        © {new Date().getFullYear()} ReplyShoot – Secure OAuth 2.0 integration.
      </Footer>
    </div>
  );
};

export default GoogleConnectPage;
